<template>
  <AuthLayout
    :sidebar-title="isAdmin ? 'Admin portal' : 'Welcome back to your care portal'"
    :sidebar-text="isAdmin ? 'Enter your password to continue. We will send a verification code to keep your account secure.' : 'Enter your password to continue. We\'ll send a verification code to keep your account secure.'"
    :support-phone="supportPhone"
    :is-admin="isAdmin"
    title="Sign in with password"
    :subtitle="identifier"
  >
    <form action="javascript:void(0)" @submit.prevent="handleSubmit">
      <div
          v-if="isPasswordReset"
          class="auth-login__success-banner"
      >
        Your password has been reset. You may now log in with your new password.
      </div>
      <div v-else-if="passwordResetChannel">
        <div class="auth-login__success-banner">
          <p>We sent a secure password reset link to your <b>{{ passwordResetChannel === 'sms' ? 'mobile phone' : 'email' }}</b>. Click the link to set a new password.</p>
          <p>You may log in with your new password here once it has been changed.</p>
        </div>
      </div>

      <input
        class="auth-login__sr-only"
        type="text"
        name="username"
        autocomplete="username"
        :value="identifier"
        readonly
        tabindex="-1"
      >

      <AuthPasswordField
        id="password-input"
        ref="passwordField"
        v-model="password"
        name="current-password"
        label="Password"
        placeholder="Enter your password"
        autocomplete="current-password"
        forgot-label="Forgot password?"
        :error="error"
        required
        @forgot="emit('forgot')"
      />

      <div v-if="!bypassMfa" class="auth-login__row-between">
        <AuthCheckbox id="remember-device-login" v-model="rememberDevice" label="Remember for 90 days" />
      </div>

      <div class="auth-login__actions">
        <AuthButton type="submit" :loading="loading" :disabled="!password">
          Sign in
        </AuthButton>
        <AuthButton v-if="!isAdmin && showMagicLink" variant="ghost" :loading="loading" @click="emit('magic-link')">
          Or send me a magic link instead
        </AuthButton>
      </div>
    </form>

    <button type="button" class="auth-login__back" :disabled="loading" @click="emit('cancel')">
      ← Back
    </button>

    <template v-if="registerUrl && !isAdmin" #footer>
      <span class="auth-login__signup-text">
        You don't have an account?
        <a :href="registerUrl" class="auth-login__link auth-login__link--plain">Sign up</a>
      </span>
    </template>
  </AuthLayout>
</template>

<script setup>
import { ref, watch } from 'vue'
import AuthLayout from '../../shared/components/AuthLayout.vue'
import AuthPasswordField from '../../shared/components/AuthPasswordField.vue'
import AuthButton from '../../shared/components/AuthButton.vue'
import AuthCheckbox from '../../shared/components/AuthCheckbox.vue'

const props = defineProps({
  identifier:     { type: String, required: true },
  loading:        { type: Boolean, default: false },
  error:          { type: String,  default: '' },
  trustDevice:    { type: Boolean, default: false },
  bypassMfa:      { type: Boolean, default: false },
  registerUrl:    { type: String,  default: '' },
  supportPhone:   { type: String,  default: '815-908-2020' },
  isAdmin:        { type: Boolean, default: false },
  showMagicLink:  { type: Boolean, default: false },
  isPasswordReset:  { type: Boolean, default: false },
  passwordResetChannel: {type: String, default: ''}
})

const emit = defineEmits(['submit', 'forgot', 'magic-link', 'cancel', 'update:trustDevice'])

const password = ref('')
const passwordField = ref(null)
const rememberDevice = ref(props.trustDevice)

watch(rememberDevice, (val) => emit('update:trustDevice', val))
watch(() => props.trustDevice, (val) => { rememberDevice.value = val })

const handleSubmit = () => {
  const inputValue = passwordField.value?.getInputElement?.()?.value ?? ''
  const resolvedPassword = password.value || inputValue

  if (!resolvedPassword) return

  password.value = resolvedPassword
  emit('submit', resolvedPassword)
}
</script>

<style scoped>
.auth-login__success-banner {
  flex-direction: column;
  margin-top: 0 !important;
}

.auth-login__success-banner p {
  margin: 0;
  padding: 0;
}
</style>
